import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Play, X, Youtube } from "lucide-react";
import { getEpisodes } from "../lib/api";

export default function Episodes() {
  const [data, setData] = useState({ episodes: [], channel_url: "", playlist_id: "" });
  const [active, setActive] = useState(null);

  useEffect(() => {
    getEpisodes().then(setData).catch(() => {});
  }, []);

  const embedSrc = active
    ? `https://www.youtube.com/embed/${active.video_id}?autoplay=1`
    : "";

  return (
    <section id="episodes" className="relative py-24 sm:py-32" data-testid="episodes-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
          <div>
            <p className="overline text-blaze mb-4">Featured on YouTube</p>
            <h2 className="font-heading font-800 tracking-tight text-4xl sm:text-5xl max-w-2xl leading-tight">
              Start with three. Subscribe for the movement.
            </h2>
          </div>
          <a
            href={data.channel_url || "#"}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-3 text-sm font-bold hover:bg-white/10 transition-colors whitespace-nowrap"
            data-testid="episodes-subscribe-btn"
          >
            <Youtube size={16} /> All Episodes
          </a>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {data.episodes.map((ep, i) => (
            <motion.button
              key={ep.id}
              onClick={() => setActive(ep)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: (i % 2) * 0.1 }}
              className="group text-left rounded-2xl bg-charcoal border border-white/10 p-6 hover:-translate-y-1 transition-transform"
              data-testid={`episode-card-${ep.id}`}
            >
              <div className="flex items-center justify-between mb-4">
                <span className="overline text-white/40">
                  S{ep.season} · Ep {ep.number}
                </span>
                <span className="rounded-full border border-blaze/40 text-blaze overline px-3 py-1">
                  {ep.category}
                </span>
              </div>
              <div className="flex items-start gap-4">
                <span className="mt-1 grid place-items-center h-12 w-12 shrink-0 rounded-full bg-white/5 border border-white/10 text-white group-hover:bg-blaze group-hover:blaze-glow transition-colors">
                  <Play size={18} className="ml-0.5" />
                </span>
                <div>
                  <h3 className="font-heading font-700 text-lg leading-snug">{ep.title}</h3>
                  <p className="mt-2 text-sm text-white/60 line-clamp-2 leading-relaxed">
                    {ep.summary}
                  </p>
                  <p className="mt-3 text-xs text-white/40 font-mono">
                    {ep.date} · {ep.duration}
                  </p>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {active && (
        <div
          className="fixed inset-0 z-[60] grid place-items-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setActive(null)}
          data-testid="episode-modal"
        >
          <div
            className="relative w-full max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="overline text-blaze">
                  S{active.season} · Ep {active.number}
                </p>
                <h3 className="font-heading font-700 text-xl mt-1">{active.title}</h3>
              </div>
              <button
                onClick={() => setActive(null)}
                className="rounded-full border border-white/20 p-2 hover:bg-white/10"
                data-testid="episode-modal-close"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="aspect-video rounded-2xl overflow-hidden border border-white/10 bg-black">
              <iframe
                title={active.title}
                src={embedSrc}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
